import { modelSaid } from "@rally/agreed";
import {
  Avatar,
  Col,
  face as faceSize,
  Icon,
  type Origin,
  Text,
  Tooltip,
  useEntrance,
  useTheme,
  useTravel,
} from "@rally/ui-kit";
import { useState } from "react";
import { formatTime } from "../../localization/format.ts";
import { say } from "../../localization/say.ts";
import { faceOfAnAgent } from "../../me/an-agents-mark.ts";
import { calledBy } from "../../me/called.ts";
import { useFace } from "../../me/face.ts";
import type { Answering, PutAway, TargetCapabilities } from "../../net/api.ts";
import { usePresent } from "../../push/here.ts";
import { FiledBody } from "../attachment/filed.tsx";
import type { Candidate } from "../composer/mentions.ts";
import type { Person } from "../composer/saying.ts";
import type { ListedFile } from "../filed.ts";
import type { Reaction } from "../reaction/reacting.ts";
import { LANE_WIDTH } from "../reading.ts";
import { TargetActions } from "../target-actions.tsx";
import { Marked } from "./marked.tsx";
import type { Taggable } from "./tags.ts";

export const RESERVED_FOR_ACTIONS = LANE_WIDTH;
const READING = 760;

function ParentQuote({ answering }: { answering: Answering }) {
  const theme = useTheme();
  return (
    <Col
      testID={`reply-quote-${answering.id}`}
      style={{
        maxWidth: "100%",
        alignSelf: "flex-start",
        flexDirection: "row",
        alignItems: "center",
        gap: theme.space[1],
        paddingHorizontal: theme.space[2],
        paddingVertical: 3,
        borderLeftWidth: 2,
        borderLeftColor: theme.color["border.strong"],
        backgroundColor: theme.color["bg.surfaceAlt"],
        borderRadius: theme.radius.sm,
      }}
    >
      <Text typeName="caption" color="text.tertiary" numberOfLines={1}>
        {answering.kind === "file" ? answering.said : `“${answering.said}”`}
      </Text>
    </Col>
  );
}

export function Said({
  who,
  text,
  at,
  from,
  arrived,
  highlighted = false,
  id,
  reactions,
  away,
  answering,
  capabilities,
  names,
  files,
  attachments = [],
  conversation,
  me,
  phone,
  grouped = false,
  onAttachmentDrawn,
  onChanged,
  onReply,
  onError,
}: {
  who: Person | undefined;
  text: string;
  at: string;
  from: Origin | null;
  arrived: boolean;

  highlighted?: boolean;
  id: string | null;
  reactions: Reaction[];
  away: PutAway;
  answering: Answering | null;
  capabilities: TargetCapabilities;
  names: readonly Candidate[];
  files: readonly Taggable[];

  attachments?: readonly ListedFile[];
  conversation: string;
  me: string;
  phone: boolean;
  grouped?: boolean;
  onAttachmentDrawn?: () => void;
  onChanged: () => void;
  onReply?: () => void;
  onError?: (cause: unknown) => void;
}) {
  const theme = useTheme();
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const face = useFace(who?.avatar);
  const present = usePresent(who?.account);
  const travel = useTravel(arrived ? from : null);
  const rise = useEntrance("rise", "fast");
  const entrance = arrived && !from ? rise : null;
  const name = calledBy(who) ?? say("chat.someone");
  const when = formatTime(at);
  return (
    <Col
      ref={arrived && from ? (travel as never) : undefined}
      testID={id ? `message-${id}` : "said-unsent"}
      {...({
        onHoverIn: () => setHovered(true),
        onHoverOut: () => setHovered(false),
        onFocus: () => setFocused(true),
        onBlur: () => setFocused(false),
        tabIndex: 0,
      } as object)}
      style={{
        flexDirection: "row",
        gap: theme.space[3],
        borderRadius: theme.radius.md,
        backgroundColor: highlighted ? theme.color.accentSoft : "transparent",
        ...(entrance as object),
      }}
    >
      {grouped ? (
        <Col style={{ width: faceSize.line, alignItems: "flex-end", paddingTop: 2 }}>
          <Text typeName="caption" color="text.tertiary" style={{ opacity: hovered || focused ? 1 : 0 }}>
            {when}
          </Text>
        </Col>
      ) : (
        <Avatar
          name={name}
          src={face}
          size={faceSize.line}
          present={present}
          {...(who?.agent ? faceOfAnAgent(who.model) : {})}
        />
      )}
      <Col style={{ flex: 1, minWidth: 0, maxWidth: READING, gap: theme.space[1] }}>
        {grouped ? null : (
          <Col style={{ flexDirection: "row", alignItems: "baseline", gap: theme.space[2] }}>
            <Text typeName="label" color="text.primary">
              {name}
            </Text>
            {who?.agent ? (
              <Tooltip content={who.model ? modelSaid(who.model) : say("chat.anAgent")}>
                <Icon name="bot" size={12} color="text.tertiary" />
              </Tooltip>
            ) : null}
            <Text typeName="caption" color="text.tertiary">
              {when}
            </Text>
          </Col>
        )}
        {answering ? <ParentQuote answering={answering} /> : null}
        {text ? <Marked text={text} who={names} files={files} conversation={conversation} me={me} /> : null}
        {attachments.map((file) => (
          <FiledBody key={file.id} file={file} conversation={conversation} onDrawn={onAttachmentDrawn} />
        ))}
        {id ? (
          <TargetActions
            target={{ kind: "message", id }}
            reactions={reactions}
            away={away}
            capabilities={capabilities}
            phone={phone}
            revealed={hovered || focused}
            onChanged={onChanged}
            onReply={onReply}
            onError={onError}
            testID={`actions-${id}`}
          />
        ) : null}
      </Col>
    </Col>
  );
}
